'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import type { DownloadsPage } from '@/lib/downloads/types';

export interface DownloadItem {
  id: string;
  projectId: string;
  name: string;
  format: string;
  createdAt: string;
  status: 'preparing' | 'downloading' | 'complete' | 'failed';
  saved: boolean;
  filename?: string;
  sizeBytes?: number;
  error?: string;
}
interface DownloadRequest {
  projectId: string;
  name: string;
  format: string;
}
interface DownloadsContextValue {
  items: DownloadItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  refresh: () => void;
  loadMore: () => void;
  run: (download: DownloadRequest, request: () => Promise<Response>) => Promise<void>;
  downloadAgain: (item: DownloadItem) => Promise<void>;
}
const DownloadsContext = createContext<DownloadsContextValue | null>(null);

function sendToBrowser(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } finally {
      anchor.remove();
    }
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

export function DownloadsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const projectId = currentProject?.id || null;
  const userId = user?.uid || null;
  const userRef = useRef(user);
  userRef.current = user;
  const projectRef = useRef(projectId);
  projectRef.current = projectId;
  const [session, setSession] = useState(() => ({
    userId,
    token: {},
    items: [] as DownloadItem[],
  }));
  if (session.userId !== userId) setSession({ userId, token: {}, items: [] });
  const activeSession = useRef(session.token);
  activeSession.current = session.token;
  const mounted = useRef(true);
  const generation = useRef(0);
  const [history, setHistory] = useState<{
    token: object;
    projectId: string;
    items: DownloadItem[];
    loading: boolean;
    error: string | null;
    nextCursor: string | null;
  } | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(
    async (cursor?: string) => {
      const currentUser = userRef.current;
      if (!currentUser || !projectId) return;
      const token = session.token;
      const requestGeneration = ++generation.current;
      const isCurrent = () =>
        mounted.current &&
        activeSession.current === token &&
        projectRef.current === projectId &&
        generation.current === requestGeneration;
      const scope = { token, projectId };
      setHistory(current => ({
        ...scope,
        items: current?.token === token && current.projectId === projectId ? current.items : [],
        nextCursor:
          current?.token === token && current.projectId === projectId ? current.nextCursor : null,
        loading: true,
        error: null,
      }));
      try {
        const authToken = await currentUser.getIdToken();
        if (!isCurrent()) return;
        const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
        const response = await fetch(
          `/api/projects/${encodeURIComponent(projectId)}/downloads${query}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        const payload = await response.json();
        if (!isCurrent()) return;
        if (!response.ok) throw new Error(payload.error || 'Couldn’t load downloads.');
        const page = payload as DownloadsPage;
        setHistory(current => {
          const previous =
            current?.token === token && current.projectId === projectId ? current.items : [];
          const merged = new Map(previous.map(item => [item.id, item]));
          for (const item of page.downloads) {
            if (item.projectId === projectId)
              merged.set(item.id, { ...item, status: 'complete', saved: true });
          }
          return {
            ...scope,
            items: [...merged.values()],
            nextCursor: page.nextCursor,
            loading: false,
            error: null,
          };
        });
      } catch (error) {
        if (isCurrent())
          setHistory(
            current =>
              current && {
                ...current,
                loading: false,
                error: error instanceof Error ? error.message : 'Couldn’t load downloads.',
              }
          );
      }
    },
    [projectId, session.token]
  );
  const refresh = useCallback(() => {
    void load();
  }, [load]);
  const invalidateHistory = useCallback(() => {
    generation.current++;
  }, []);
  useEffect(() => {
    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      invalidateHistory();
      window.removeEventListener('focus', refresh);
    };
  }, [refresh, invalidateHistory]);

  const transfer = useCallback(
    async (item: DownloadItem, request: () => Promise<Response>) => {
      const token = session.token;
      const isActive = () => mounted.current && activeSession.current === token;
      if (!isActive()) return;
      let currentItem = item;
      setSession(current => ({
        ...current,
        items: [item, ...current.items.filter(value => value.id !== item.id)],
      }));
      const update = (patch: Partial<DownloadItem>) => {
        if (!isActive()) return;
        const previousId = currentItem.id;
        currentItem = { ...currentItem, ...patch };
        const updated = currentItem;
        setSession(current => ({
          ...current,
          items: current.items.map(value => (value.id === previousId ? updated : value)),
        }));
      };
      try {
        const response = await request();
        if (!isActive()) return;
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'Export failed. Try exporting again.');
        }
        const savedId = response.headers.get('x-attune-download-id');
        const filename =
          response.headers.get('content-disposition')?.match(/filename="([^"]+)"/i)?.[1] ||
          item.filename ||
          `${item.name.replace(/[/\\]/g, '-')}.${item.format}`;
        update({
          status: 'downloading',
          filename,
          ...(savedId
            ? {
                id: savedId,
                saved: true,
                createdAt: response.headers.get('x-attune-download-created-at') || item.createdAt,
              }
            : {}),
        });
        const blob = await response.blob();
        if (!isActive()) return;
        sendToBrowser(blob, filename);
        update({ status: 'complete', sizeBytes: blob.size, error: undefined });
      } catch (error) {
        update({
          status: 'failed',
          error: error instanceof Error ? error.message : 'Download failed. Try again.',
        });
        throw error;
      }
    },
    [session.token]
  );
  const run = useCallback(
    async (download: DownloadRequest, request: () => Promise<Response>) => {
      if (!download.projectId || !userRef.current)
        throw new Error('Select a project to export files.');
      await transfer(
        {
          ...download,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          status: 'preparing',
          saved: false,
        },
        request
      );
    },
    [transfer]
  );
  const downloadAgain = useCallback(
    async (item: DownloadItem) => {
      const currentUser = userRef.current;
      if (!currentUser || !item.saved || item.projectId !== projectRef.current) return;
      await transfer({ ...item, status: 'downloading', error: undefined }, async () => {
        const token = await currentUser.getIdToken();
        return fetch(
          `/api/projects/${encodeURIComponent(item.projectId)}/downloads/${encodeURIComponent(item.id)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      });
    },
    [transfer]
  );

  const currentHistory =
    session.userId === userId && history?.token === session.token && history.projectId === projectId
      ? history
      : null;
  const merged = new Map((currentHistory?.items || []).map(item => [item.id, item]));
  if (session.userId === userId) {
    for (const item of session.items) if (item.projectId === projectId) merged.set(item.id, item);
  }
  const items = [...merged.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const loading = Boolean(userId && projectId && (!currentHistory || currentHistory.loading));
  const loadMore = () => {
    if (!loading && currentHistory?.nextCursor) void load(currentHistory.nextCursor);
  };
  return (
    <DownloadsContext.Provider
      value={{
        items,
        loading,
        error: currentHistory?.error || null,
        hasMore: Boolean(currentHistory?.nextCursor),
        refresh,
        loadMore,
        run,
        downloadAgain,
      }}
    >
      {children}
    </DownloadsContext.Provider>
  );
}

export function useDownloads() {
  const value = useContext(DownloadsContext);
  if (!value) throw new Error('useDownloads requires DownloadsProvider');
  return value;
}
